/**
 * Windows display-scaling neutralizer (desktop portals only).
 *
 * Windows at 125/150/175% shrinks the CSS viewport and enlarges everything, so
 * EdgeLMS renders bigger/cramped vs 100%. We counter that with a root
 * `zoom: 1/devicePixelRatio` so pages lay out and render exactly like 100%.
 *
 * Strictly guarded so we NEVER shrink legitimately high-DPI screens or touch UIs:
 *   - only when devicePixelRatio is FRACTIONAL (1.25/1.5/1.75 …) — Mac Retina and
 *     native displays report INTEGER ratios (1, 2, 3) and are left untouched;
 *   - only on Windows (so a zoomed Mac is never affected);
 *   - only with a fine (mouse) pointer — phones/tablets excluded;
 *   - only on desktop portals — the caller passes enabled=false for rider/hub.
 *
 * Known limitation: the browser exposes only devicePixelRatio, which also moves
 * with manual browser zoom (Ctrl +/-). On Windows that manual zoom is treated
 * the same as OS scaling and gets neutralised — acceptable for the OS-scaling
 * goal this feature targets.
 */

const isWindows = () => {
  const p =
    (typeof navigator !== "undefined" &&
      ((navigator.userAgentData && navigator.userAgentData.platform) || navigator.platform)) ||
    "";
  return /win/i.test(p);
};

// Fractional ratio > 1 → produced by OS/browser scaling, not a native integer DPR.
const isFractionalDpr = (dpr) => dpr > 1 && Math.abs(dpr - Math.round(dpr)) > 0.01;

const hasFinePointer = () => {
  try {
    return window.matchMedia("(pointer: fine)").matches;
  } catch {
    return true;
  }
};

/** The zoom to apply (counteracts DPR), or null when no scaling should happen. */
export function computeDisplayZoom(enabled) {
  if (enabled === false) return null;
  const dpr = (typeof window !== "undefined" && window.devicePixelRatio) || 1;
  if (!isWindows() || !isFractionalDpr(dpr) || !hasFinePointer()) return null;
  return Math.round((1 / dpr) * 10000) / 10000; // trim sub-pixel jitter
}

/** Apply (or clear) the counter-zoom on the document root. Returns the zoom used. */
export function applyDisplayScale(enabled) {
  if (typeof document === "undefined") return null;
  const el = document.documentElement;
  const zoom = computeDisplayZoom(enabled);
  if (zoom && zoom !== 1) {
    el.style.zoom = String(zoom);
    // Exposed so full-height (100vh/100dvh) utilities can divide by the zoom and
    // still fill the real viewport — otherwise a root zoom leaves empty space
    // below full-screen backgrounds (see index.css [data-display-scaled] rules).
    el.style.setProperty("--display-scale", String(zoom));
    el.setAttribute("data-display-scaled", String(zoom));
  } else {
    el.style.zoom = "";
    el.style.removeProperty("--display-scale");
    el.removeAttribute("data-display-scaled");
  }
  return zoom;
}
